/**
 * TypeScript type definitions for workflow-related data structures.
 * These types match the backend Pydantic schemas.
 */

import React from 'react';

export enum ExecutionStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

export enum InputType {
  TEXT = 'text',
  NUMBER = 'number',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  CHECKBOX = 'checkbox',
  FILE = 'file',
  CSV = 'csv',
}

export enum OutputType {
  CSV = 'csv',
  JSON = 'json',
  TEXT = 'text',
  FILE = 'file',
  FILES = 'files',
}

export interface InputSpec {
  type: InputType;
  label: string;
  required: boolean;
  default?: any;
  description: string;
  options?: string[];
  condition?: string;
  from_step?: string;
  output_key?: string;
  validation?: Record<string, any>;
  sensitive: boolean;
}

export interface OutputSpec {
  type: OutputType;
  description: string;
  path?: string;
  expected_columns?: string[];
  formats?: string[];
}

export interface WorkflowStep {
  step_id: string;
  name: string;
  script_path: string;
  description: string;
  inputs: Record<string, InputSpec>;
  outputs: Record<string, OutputSpec>;
  estimated_time: string;
  dependencies: string[];
}

export interface WorkflowBase {
  name: string;
  description: string;
  version: string;
  created_by: string;
  tags: string[];
  estimated_duration: string;
}

export interface WorkflowCreate extends WorkflowBase {
  workflow_id: string;
  steps: WorkflowStep[];
  dependencies: Record<string, string[]>;
  error_handling: Record<string, any>;
  notifications: Record<string, any>;
}

export interface WorkflowUpdate {
  name?: string;
  description?: string;
  version?: string;
  tags?: string[];
  steps?: WorkflowStep[];
  dependencies?: Record<string, string[]>;
  error_handling?: Record<string, any>;
  notifications?: Record<string, any>;
}

export interface WorkflowSummary extends WorkflowBase {
  workflow_id: string;
  steps_count: number;
  created_at: string;
  updated_at: string;
}

export interface Workflow extends WorkflowBase {
  workflow_id: string;
  steps: WorkflowStep[];
  dependencies: Record<string, string[]>;
  error_handling: Record<string, any>;
  notifications: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface WorkflowImportResponse {
  workflow: Workflow;
  message: string;
  warnings: string[];
}

export interface ExecutionResult {
  success: boolean;
  output_data: Record<string, any>;
  error_message: string;
  execution_time: number;
  logs: string[];
  stdout: string;
  stderr: string;
}

export interface StepResult {
  step_id: string;
  status: ExecutionStatus;
  result?: ExecutionResult;
  timestamp: string;
  inputs_used: Record<string, any>;
}

export interface WorkflowExecution {
  execution_id: string;
  workflow_id: string;
  status: ExecutionStatus;
  current_step: number;
  step_results: Record<string, StepResult>;
  started_at?: string;
  completed_at?: string;
  error_message: string;
}

export interface ExecutionSummary {
  execution_id: string;
  workflow_id: string;
  status: ExecutionStatus;
  started_at?: string;
  completed_at?: string;
  duration?: number;
  steps_completed: number;
  steps_total: number;
}

export interface ExecutionCreate {
  workflow_id: string;
  inputs?: Record<string, any>;
}

export interface StepExecuteRequest {
  inputs: Record<string, any>;
  skip_validation?: boolean;
}

export interface StepExecuteResponse {
  success: boolean;
  step_result: StepResult;
  execution_updated: WorkflowExecution;
}

export interface ExecutionStats {
  total_executions: number;
  completed_executions: number;
  failed_executions: number;
  running_executions: number;
  success_rate: number;
  average_duration?: number;
}

export interface WebSocketMessage {
  type: string;
  execution_id: string;
  data: Record<string, any>;
  timestamp: string;
}

export interface ScriptMetadata {
  name: string;
  description: string;
  inputs: Record<string, InputSpec>;
  outputs: Record<string, OutputSpec>;
  script_path: string;
  estimated_time: string;
  dependencies: string[];
}

// API Request/Response types
export interface APIResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  status: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// UI State types
export interface UIState {
  loading: boolean;
  error: string | null;
  selectedWorkflow: Workflow | null;
  currentExecution: WorkflowExecution | null;
}

// Form types
export interface FormField {
  name: string;
  type: InputType;
  label: string;
  required: boolean;
  value: any;
  error?: string;
  options?: string[];
  disabled?: boolean;
}

export interface FormData {
  [key: string]: any;
}

// Navigation types
export interface NavigationItem {
  name: string;
  href: string;
  icon?: React.ComponentType;
  current: boolean;
}